// Serialising the object clipboard so it can travel through the SYSTEM clipboard
// (not just an in-app variable), which is what lets a copy in one browser window
// paste into another. The payload is our own JSON tagged with a magic marker;
// anything else on the clipboard (plain text, an image, another app's data) is
// ignored by the object paste, so deserialise returns null for it.
//
// Pure and DOM-free on purpose (unit-tested): the navigator.clipboard plumbing
// and the in-app state live in main.ts.
import type {
  BackgroundImage,
  DrawingGroup,
  ObjectConstraint,
} from '../shared/types';
import {
  OBJECT_ARRAY,
  type ObjectArrays,
  STORED_KINDS,
} from './drawingObjects';

/** The copied objects under their kinds' array keys — the list comes from the
 *  kinds' adapters (drawingObjects.ts OBJECT_ARRAY), so a new kind travels too.
 *  @1 shipped with parts, connections, shapes, walls, texts, floors and
 *  underlays; every other kind was added since (the annotations, then sections
 *  and ceiling plans): an older payload simply lacks them (read back as empty)
 *  and an older window ignores them — no version bump. */
export interface ClipboardPayload extends ObjectArrays {
  backgroundImages: BackgroundImage[];
  /** The groups copied whole (older payloads lack them: read back as empty). */
  groups: DrawingGroup[];
  /** The constraints between copied objects (older payloads lack them: empty). */
  constraints: ObjectConstraint[];
  /** The copied objects' and groups' ids in their stacking order, bottom first,
   *  so the copies keep it inside a copied group or room (older payloads: empty). */
  stack?: string[];
}

// Versioned: bump the number if the payload shape ever changes incompatibly, so
// an older window silently ignores a newer window's copy instead of mis-pasting.
export const CLIP_MAGIC = 'stenalcad/clipboard@1';

export function serializeClipboard(cb: ClipboardPayload): string {
  return JSON.stringify({ magic: CLIP_MAGIC, data: cb });
}

/** Parse a system-clipboard string back into a payload, or null when it isn't
 *  ours. Missing arrays are coerced to empty so a partial payload can't throw
 *  downstream. */
export function deserializeClipboard(text: string): ClipboardPayload | null {
  const s = (text || '').trim();
  // Cheap reject before the parse: our payload is a JSON object carrying the marker.
  if (!s.startsWith('{') || !s.includes(CLIP_MAGIC)) return null;
  let obj: { magic?: unknown; data?: Record<string, unknown> };
  try { obj = JSON.parse(s) as typeof obj; } catch { return null; }
  if (!obj || obj.magic !== CLIP_MAGIC || !obj.data || typeof obj.data !== 'object') return null;
  const d = obj.data;
  const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const objects = Object.fromEntries(STORED_KINDS.map((k) => [OBJECT_ARRAY[k], arr(d[OBJECT_ARRAY[k]])])) as unknown as ObjectArrays;
  return {
    ...objects,
    backgroundImages: arr<BackgroundImage>(d.backgroundImages),
    groups: arr<DrawingGroup>(d.groups),
    constraints: arr<ObjectConstraint>(d.constraints),
    ...(Array.isArray(d.stack) ? { stack: d.stack.filter((x): x is string => typeof x === 'string') } : {}),
  };
}

/** Whether a payload carries anything paste-able (a connection only comes with
 *  the objects it joins). */
export function clipboardHasContent(cb: ClipboardPayload): boolean {
  return cb.backgroundImages.length > 0 || STORED_KINDS.some((k) => k !== 'conn' && ((cb[OBJECT_ARRAY[k]] as unknown[] | undefined)?.length ?? 0) > 0);
}
